import { useEffect, useContext, useState, useMemo } from "react";
import sortBy from "lodash/sortBy";

import { useStoreContext } from "@components/store/provider";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import ProductCarousel from "@components/store/product-detail/shared/product-detail-carousel";
import ProductDetailHeader from "@components/store/product-detail/shared/product-detail-header";
import VendorHolidayMessage from "@components/store/product-detail/shared/vendor-holiday-message";
import AddToBagKitItem from "@components/store/product-detail/shared/add-to-bag-kit-item";
import CartFloater from "@components/widgets/cart-floater";
import QuantitySelector from "@components/store/product-detail/shared/quantity-selector";
import SizeChartModal from "@components/modals/size-chart";
import { ExternalButton } from "@sharedComponents/buttons";
import ToolTip from "@stadium/ui/src/components/tooltip/tooltip";
import { ProductDiscountTable } from "@components/store/product-detail/shared/discount-table";
import ProductInfoSection from "@components/store/product-detail/shared/product-info-section";
import ProductDescriptionAccordion from "@components/store/product-detail/shared/accordion/product-description";

import { useModal } from "@stadium/hooks/lib/use-modal";
import usePopulateKitItems from "@hooks/kit/use-populate-kit";
import { useFetchInventoryDetails } from "@hooks/product-detail/use-fetch-inventory";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import {
  processVariantImages,
  getProductColorsAndSizes,
  getProductAllVariantImages,
  getVariantDiscountTableData,
} from "@helpers/product-details";
import { getPriceFromBulkProduct } from "@stadium/helpers/product/utils";
import { pluralizeWord } from "@stadium/helpers/string";
import { getMasterVariant } from "@helpers/products/search";

import { WAREHOUSE_PRODUCT, DEFAULT_QUANTITY } from "@constants/products/detail";
import { SIZE_CHART_MODAL } from "@constants/modal-types";
import { weglotNoTranslationClassName } from "@stadium/constants";

import styles from "./main.module.scss";
import Multimedia from "@stadium/ui/src/components/multimedia/multimedia";

const DEFAULT_OBJ = {};

const BundleProductDetail = ({ product }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY);

  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { fetchInventoryDetails, variantData } = useFetchInventoryDetails();

  const { updateItemInCart, loading, setShowItemAddedMsg, showItemAddedMsg } =
    usePopulateKitItems();

  const { openModal: openSizeChartModal, renderModal: renderSizeChartModal } = useModal(
    SIZE_CHART_MODAL,
    SizeChartModal,
  );

  const { formatPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();

  const { isGlobalCountry } = useStoreContext();

  const {
    id,
    name,
    priceIn,
    vendorByCountry,
    type,
    variantsIncludingMaster = [],
    sizeChartEnabled,
    sizeChart,
    sizeChartLink,
    allVariantsAvailableGlobally,
    bulkAvailable,
    masterVariant: productMasterVariant,
    hasTierPricing,
  } = product;

  useEffect(() => {
    fetchInventoryDetails([product]);
  }, [id]);

  const { colorOptionsValue, sizeOptionsValue } = useMemo(
    () => getProductColorsAndSizes(product),
    [id],
  );

  const sortedSizeOptionValue = useMemo(
    () => sortBy(sizeOptionsValue, ({ position }) => position),
    [sizeOptionsValue],
  );

  const sortedColorOptionValue = useMemo(
    () => sortBy(colorOptionsValue, ({ position }) => position),
    [colorOptionsValue],
  );

  const masterVariant = useMemo(() => getMasterVariant(variantsIncludingMaster), [id]);
  const productImages = useMemo(() => getProductAllVariantImages(variantsIncludingMaster), [id]);

  const getDisplayPrice = () => {
    if (bulkAvailable || hasTierPricing) {
      const value = getPriceFromBulkProduct(productMasterVariant, quantity);
      return formatPriceInDisplayStoreCurrency(value);
    }
    return formatPriceInDisplayStoreCurrency(masterVariant?.priceIn || priceIn);
  };

  const productQuantity = variantData[id];
  const productQuantityMapping = productQuantity?.total;
  const { inLocker, inTransit } = productQuantityMapping || DEFAULT_OBJ;

  const displayPrice = (
    <span>
      <span className={weglotNoTranslationClassName}>{getDisplayPrice()}</span> per unit
    </span>
  );
  const isWarehouseProduct = WAREHOUSE_PRODUCT === type;

  const discountTableData = useMemo(
    () =>
      getVariantDiscountTableData({
        masterVariant: productMasterVariant,
      }),
    [id],
  );

  const renderColorOptions = () =>
    Boolean(sortedColorOptionValue?.length) && (
      <div className={styles.colorCodeContainer}>
        <h6 className={styles.subHeading}>
          Available {pluralizeWord("color", { count: sortedColorOptionValue.length })}
        </h6>
        <div className={styles.colorCodeCircles}>
          {sortedColorOptionValue.map(({ name, id, variant = {}, presentation }) => {
            const { images = [] } = variant;
            const image = images?.[0];
            const qty = productQuantity?.[id] || 0;

            return (
              <div className={styles.colorContainer} key={id}>
                <ToolTip
                  content={presentation}
                  isLightTheme={true}
                  placement="top-start"
                  arrow={true}
                  customClassName={styles.colorVariantTippy}
                >
                  <div className={styles.customRadio}>
                    <Multimedia
                      type="image"
                      alt={presentation}
                      src={image}
                      height={66}
                      width={66}
                    />
                  </div>
                </ToolTip>
                {!sortedSizeOptionValue?.length && isWarehouseProduct && Boolean(inLocker) && (
                  <div className={styles.colorCircleQty}>{qty}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );

  const renderSizeChartCTA = () => {
    if (sizeChartEnabled) {
      return (
        <span onClick={openSizeChartModal} className={styles.sizeGuide}>
          Size Guide
        </span>
      );
    }
    if (sizeChartLink) {
      return (
        <ExternalButton
          styleType="link"
          href={sizeChartLink}
          className={styles.sizeGuide}
          target="_blank"
        >
          Size Guide
        </ExternalButton>
      );
    }
    return null;
  };

  const renderSizeOptions = () => {
    if (!sortedSizeOptionValue?.length) {
      return;
    }

    const availableSizes = sortedSizeOptionValue.map(({ presentation }) => presentation).join(", ");

    return (
      <div className={styles.sizesContainer}>
        <div className={styles.sizeBlock}>
          <h6 className={styles.subHeading}>
            Available {pluralizeWord("size", { count: sortedSizeOptionValue.length })}
            &nbsp;
            {Boolean(inLocker || inTransit) &&
              `in ${inLocker ? "locker" : "transit"} (${inLocker || inTransit})`}
          </h6>
          {renderSizeChartCTA()}
        </div>
        {isWarehouseProduct && Boolean(inLocker) ? (
          <div className={styles.sizeRow}>
            {sortedSizeOptionValue.map(({ id, presentation }) => {
              const qty = productQuantity?.[id] || 0;

              return (
                <div className={styles.sizeColumn} key={presentation}>
                  <div key={presentation} className={styles.sizeHeading}>
                    {presentation}
                  </div>
                  <div className={styles.sizeQty}>{qty}</div>
                </div>
              );
            })}{" "}
          </div>
        ) : (
          <span className={styles.availableSizes}>{availableSizes}</span>
        )}
      </div>
    );
  };

  const onSuccess = () => {
    setShowItemAddedMsg(true);
    setTimeout(() => {
      setShowItemAddedMsg(false);
    }, 3000);
  };

  const renderQuantitySelector = () => (
    <div className={styles.qtyColLeft}>
      <span className={styles.qtyWrapper}>Quantity</span>
      <QuantitySelector
        quantity={quantity}
        setQuantity={setQuantity}
        className="defaultStyle"
        disabled={!product.purchasable}
      />
    </div>
  );

  const renderProductDetailHeader = () => (
    <ProductDetailHeader displayPrice={displayPrice} showProductName product={product} />
  );

  const renderProductDetails = () => (
    <div className={styles.boxContainerFluid}>
      {!isMobileDevice && renderProductDetailHeader()}
      <VendorHolidayMessage vendor={vendorByCountry} />
      {isWarehouseProduct && Boolean(inLocker || inTransit) && (
        <div className={styles.totalQuantity}>
          Total quantity in {inLocker ? "locker" : "transit"}: {inLocker || inTransit}
        </div>
      )}
      {renderColorOptions()}
      {renderSizeOptions()}
      <div className={styles.quantityBundle}>
        {renderQuantitySelector()}
        <AddToBagKitItem
          product={product}
          btnClass={styles.addBundleBtn}
          isLoading={loading}
          onAdd={updateItemInCart}
          onSuccess={onSuccess}
          quantity={quantity}
          isDisabled={!product.purchasable}
        />
        {isGlobalCountry && allVariantsAvailableGlobally === false && (
          <span className={styles.variantMsgToolTip}>
            <ToolTip
              content="Some variants might not be available in specific countries."
              infoIcon
              isLightTheme
              arrow
              asResponsiveModal
            />
          </span>
        )}
      </div>
      <ProductDescriptionAccordion product={product} />
      <ProductInfoSection product={product} />
      {(bulkAvailable || hasTierPricing) && discountTableData?.length > 1 && (
        <ProductDiscountTable tableData={discountTableData} />
      )}
    </div>
  );

  return (
    <div>
      {isMobileDevice && <h1 className={styles.h1Heading}>{product?.name}</h1>}
      <div
        data-type="product-row"
        className={`${styles.rowProductDetail} ${styles.rowWithV2Enabled}`}
      >
        {isMobileDevice && renderProductDetailHeader()}
        <div className={styles.productCarousalWrapper}>
          <ProductCarousel
            sliderImages={processVariantImages(productImages)}
            selectedImageIndex={selectedImageIndex}
            setSelectedImageIndex={setSelectedImageIndex}
            imageMagnify
            carouselContainerClass={styles.withVerticalSlider}
            thumbnailContainterClass={styles.thumbBlock}
          />
        </div>
        <article className={styles.productDetail}>
          <div className={styles.productDetailBox}>{renderProductDetails()}</div>
        </article>
      </div>
      {sizeChartEnabled &&
        renderSizeChartModal(
          {
            data: { productName: name, sizeChart, sizeChartLink },
          },
          false,
        )}
      <CartFloater showItemAddedMsg={showItemAddedMsg} />
    </div>
  );
};

export default BundleProductDetail;
